/**
 * Round 108 (2026-02-14) — Mission Handoff Context.
 *
 * Shared React context that carries the customer's approved
 * mission draft + strategist output from the Dashboard into any
 * specialized builder modal (GenerateContentModal, LeadMagnetBuilder,
 * EmailSequenceBuilder, SalesPageBuilder, BuildOfferModal,
 * BusinessEssentialsBuilder, MonetizationPlanModal, …).
 *
 * The Provider is FULLY CONTROLLED — the parent owns the handoff
 * state and passes it in via `handoff`. This avoids the class of
 * bugs where the Provider caches the initial prop with
 * `useState(initialHandoff)` and drifts when the parent updates.
 *
 * Consumers get:
 *   handoff = { asset_id, asset_type, mission_id, mission_title,
 *               approved_edits, strategist_outputs, asset_payload,
 *               business_id, business_context, niche, target_audience,
 *               preferred_platform, publishing_instructions, brand_voice }
 *             | null
 *
 *   pickField(...keys) — field-precedence resolver:
 *                        approved_edits > strategist_outputs
 *                        > asset_payload > business_context.
 *                        Returns the first non-empty match.
 */
import React, { createContext, useCallback, useContext, useMemo } from 'react';

const MissionHandoffContext = createContext({
  handoff: null,
  pickField: () => null,
});

const nonEmpty = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

export const MissionHandoffProvider = ({ children, handoff = null, initialHandoff = null }) => {
  // Round 108 — accept either `handoff` (preferred, fully-controlled)
  // or `initialHandoff` (legacy alias). The parent DashboardPage
  // owns the state; this Provider is a passthrough.
  const value = useMemo(() => {
    const currentHandoff = handoff !== undefined && handoff !== null ? handoff : initialHandoff;
    const pickField = (...keys) => {
      if (!currentHandoff) return null;
      const sources = [
        currentHandoff.approved_edits || {},
        currentHandoff.strategist_outputs || {},
        currentHandoff.asset_payload || {},
        currentHandoff.business_context || {},
      ];
      for (const src of sources) {
        for (const key of keys) {
          const v = src[key];
          if (nonEmpty(v)) return v;
        }
      }
      return null;
    };
    return { handoff: currentHandoff, pickField };
  }, [handoff, initialHandoff]);

  return (
    <MissionHandoffContext.Provider value={value}>
      {children}
    </MissionHandoffContext.Provider>
  );
};

export const useMissionHandoff = () => useContext(MissionHandoffContext);

export default MissionHandoffContext;
